import type { CurrencyCode } from '@tectonics/prs/money';

export type Country = typeof registry[number]['abbreviation'];

export type CountryNationality = typeof registry[number]['nationality'];

export type CountryName = typeof registry[number]['name'];

export type MoneyFormat = 'dollar' | 'latam';

type RegistryEntry = {
  abbreviation: string;
  name: string;
  nationality: string;
  currency: CurrencyCode;
  moneyFormat: MoneyFormat;
  instalmentsValueText?: 'times' | 'months';
  cardExpiryPlaceholder?: 'MM/AAAA' | 'MM/YYYY';
};

export function getCountryName(abbreviation: Country): CountryName {
  const country = registry.find((entry) => entry.abbreviation === abbreviation);

  if (!country) throwCountryNotFound(abbreviation);

  return country.name;
}

export function getCountryNationality(abbreviation: Country): CountryNationality {
  const country = registry.find((entry) => entry.abbreviation === abbreviation);

  if (!country) throwCountryNotFound(abbreviation);

  return country.nationality;
}

export function getCountryInstalmentsValueText(abbreviation: Country): 'times' | 'months' | undefined {
  return getRegistryEntry(abbreviation).instalmentsValueText;
}

export function getCountryCardExpiryPlaceholder(abbreviation: Country): 'MM/AAAA' | 'MM/YYYY' | undefined {
  return getRegistryEntry(abbreviation).cardExpiryPlaceholder;
}

export function getCountryCurrency(abbreviation: Country): CurrencyCode {
  return getRegistryEntry(abbreviation).currency;
}

export function getCountryMoneyFormat(abbreviation: Country): MoneyFormat {
  return getRegistryEntry(abbreviation).moneyFormat;
}

function getRegistryEntry(abbreviation: Country): RegistryEntry {
  const country: RegistryEntry | undefined = registry.find((entry) => entry.abbreviation === abbreviation);

  if (!country) throwCountryNotFound(abbreviation);

  return country;
}

function throwCountryNotFound(abbreviation: string): never {
  throw new Error(`Country with abbreviation "${abbreviation}" not found`);
}

const registry = [
  { abbreviation: 'br', name: 'brazil', nationality: 'brazilian', currency: 'BRL', moneyFormat: 'latam', instalmentsValueText: 'times', cardExpiryPlaceholder: 'MM/AAAA' },
  { abbreviation: 'mx', name: 'mexico', nationality: 'mexican', currency: 'MXN', moneyFormat: 'dollar', instalmentsValueText: 'months', cardExpiryPlaceholder: 'MM/AAAA' },
  { abbreviation: 'ar', name: 'argentina', nationality: 'argentine', currency: 'ARS', moneyFormat: 'latam', instalmentsValueText: 'times', cardExpiryPlaceholder: 'MM/AAAA' },
  { abbreviation: 'co', name: 'colombia', nationality: 'colombian', currency: 'COP', moneyFormat: 'latam', instalmentsValueText: 'times', cardExpiryPlaceholder: 'MM/AAAA' },
  { abbreviation: 'cl', name: 'chile', nationality: 'chilean', currency: 'CLP', moneyFormat: 'latam', instalmentsValueText: 'times', cardExpiryPlaceholder: 'MM/AAAA' },
  { abbreviation: 'pe', name: 'peru', nationality: 'peruvian', currency: 'PEN', moneyFormat: 'latam', instalmentsValueText: 'times', cardExpiryPlaceholder: 'MM/AAAA' },
  { abbreviation: 'ec', name: 'ecuador', nationality: 'ecuadorian', currency: 'USD', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'bo', name: 'bolivia', nationality: 'bolivian', currency: 'BOB', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'uy', name: 'uruguay', nationality: 'uruguayan', currency: 'UYU', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'py', name: 'paraguay', nationality: 'paraguayan', currency: 'PYG', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'cr', name: 'costa rica', nationality: 'costaRican', currency: 'CRC', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'pa', name: 'panama', nationality: 'panamanian', currency: 'PAB', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'gt', name: 'guatemala', nationality: 'guatemalan', currency: 'GTQ', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'sv', name: 'el salvador', nationality: 'elSalvador', currency: 'USD', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'do', name: 'dominican republic', nationality: 'dominican', currency: 'DOP', moneyFormat: 'latam', instalmentsValueText: 'times' },
  { abbreviation: 'za', name: 'south africa', nationality: 'southAfrican', currency: 'ZAR', moneyFormat: 'dollar', cardExpiryPlaceholder: 'MM/YYYY' },
  { abbreviation: 'ke', name: 'kenya', nationality: 'kenyan', currency: 'KES', moneyFormat: 'dollar', cardExpiryPlaceholder: 'MM/YYYY' },
  { abbreviation: 'ng', name: 'nigeria', nationality: 'nigerian', currency: 'NGN', moneyFormat: 'dollar', cardExpiryPlaceholder: 'MM/YYYY' },
  { abbreviation: 'in', name: 'india', nationality: 'india', currency: 'INR', moneyFormat: 'dollar' },
  { abbreviation: 'ph', name: 'philippines', nationality: 'philippines', currency: 'PHP', moneyFormat: 'latam' },
  { abbreviation: 'tz', name: 'tanzania', nationality: 'tanzanian', currency: 'TZS', moneyFormat: 'dollar' },
  { abbreviation: 'ug', name: 'uganda', nationality: 'uganda', currency: 'UGX', moneyFormat: 'dollar' },
  { abbreviation: 'zm', name: 'zambia', nationality: 'zambia', currency: 'ZMW', moneyFormat: 'dollar' },
  { abbreviation: 'eg', name: 'egypt', nationality: 'egypt', currency: 'EGP', moneyFormat: 'dollar' },
  { abbreviation: 'gh', name: 'ghana', nationality: 'ghana', currency: 'GHS', moneyFormat: 'dollar' },
  { abbreviation: 'id', name: 'indonesia', nationality: 'indonesian', currency: 'IDR', moneyFormat: 'latam' },
  { abbreviation: 'jm', name: 'jamaica', nationality: 'jamaican', currency: 'JMD', moneyFormat: 'dollar' },
  { abbreviation: 'th', name: 'thailand', nationality: 'thai', currency: 'THB', moneyFormat: 'dollar' },
  { abbreviation: 'tr', name: 'turkey', nationality: 'turkish', currency: 'TRY', moneyFormat: 'latam' },
  { abbreviation: 'my', name: 'malaysia', nationality: 'malaysian', currency: 'MYR', moneyFormat: 'dollar' },
  { abbreviation: 'ci', name: 'cote divoire', nationality: 'ivorian', currency: 'XOF', moneyFormat: 'dollar' },
  { abbreviation: 'sn', name: 'senegal', nationality: 'senegalese', currency: 'XOF', moneyFormat: 'dollar' },
] as const satisfies readonly RegistryEntry[];

export const COUNTRY_ABBREVIATIONS: readonly Country[] = registry.map((entry) => entry.abbreviation);
